import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { FleetLivePanel, FleetLiveVis } from '../lib/fleet-live-vis';
import { FleetLiveIcon } from './FleetLiveIcon';
import { FleetLivePowerIcon } from './FleetLivePowerIcon';

// Fleet Live's dock bar (#6778, #6779; prototype `.fl-dock`, `:3471-3480`): the live pulse,
// the h2, "Streaming N machines" (`machines.online_total`; absent when it was not sent), one
// `role="switch"` per panel (Core, Command Deck, Topology), "Sound" / "Muted" and "Hide all"
// / "Show all". All viewer preferences, not writes, so every role gets them.

export type DockSwitch = { readonly key: FleetLivePanel; readonly label: string };

export function FleetLiveDock(props: {
  readonly headingId: string;
  readonly streaming: string | null;
  readonly switches: readonly DockSwitch[];
  readonly vis: FleetLiveVis;
  readonly onToggle: (key: FleetLivePanel) => void;
  readonly onAll: () => void;
  /** Fleet page sounds on (`fleet-live-sound.ts`). */
  readonly sound: boolean;
  readonly onSound: () => void;
}): JSX.Element {
  const anyShown = (): boolean => props.switches.some((s) => props.vis[s.key]);
  return (
    <div class="fleet-live__dock">
      <div class="fleet-live__heading">
        <span class="fleet-live__pulse" aria-hidden="true" />
        <h2 id={props.headingId}>{t('fleet.live.heading')}</h2>
        <Show when={props.streaming}>
          {(line) => <span class="fleet-live__sub">{line()}</span>}
        </Show>
      </div>
      <fieldset class="fleet-live__switches">
        <legend class="sr-only">{t('fleet.live.switches')}</legend>
        <For each={props.switches}>
          {(s) => (
            <button
              type="button"
              class="fleet-live__switch"
              role="switch"
              aria-checked={props.vis[s.key]}
              onClick={() => props.onToggle(s.key)}
            >
              <span class="fleet-live__switch-dot" aria-hidden="true" />
              <span>{s.label}</span>
            </button>
          )}
        </For>
        <button
          type="button"
          class="fleet-live__snd"
          classList={{ 'fleet-live__snd--off': !props.sound }}
          aria-pressed={props.sound}
          title={t('fleet.live.sound_title')}
          onClick={() => props.onSound()}
        >
          <FleetLiveIcon name={props.sound ? 'sound' : 'mute'} />
          <span>{props.sound ? t('fleet.live.sound') : t('fleet.live.muted')}</span>
        </button>
        <button type="button" class="fleet-live__all" onClick={() => props.onAll()}>
          <FleetLivePowerIcon />
          <span>{anyShown() ? t('fleet.live.hide_all') : t('fleet.live.show_all')}</span>
        </button>
      </fieldset>
    </div>
  );
}
